import { useEffect, useState } from 'react';
import { formatAddress } from '@zg-rentals/web-base';
import { useRentalManagerRentPaymentWebApiHandler_getListingsEligibleForPayments } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import {
  Box,
  Button,
  ButtonGroup,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';
import { useAuth } from '../../../AuthProvider';

import { client } from '../../../rentalManagerClient';
import { emitClickContinueEvent } from '../../../analytics/events';

type Props = {
  onClose: () => void;
  onAddPropertyClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const SelectProperty = ({ onClose, onAddPropertyClick }: Props) => {
  const { data } = useRentalManagerRentPaymentWebApiHandler_getListingsEligibleForPayments({ httpClient: client });
  const { user } = useAuth();
  const [selectedListingAlias, setSelectedListingAlias] = useState('');

  const options = data?.listings.map((listing) => (
    <option key={listing.listingAlias} value={listing.listingAlias}>
      {formatAddress(listing.address).formattedAddress}
    </option>
  ));

  useEffect(() => {
    if (data?.listings?.[0].listingAlias) {
      setSelectedListingAlias(data?.listings?.[0].listingAlias);
    }
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <Modal
      size="md"
      open={true}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      header={<Heading level={2}>Start collecting payments online</Heading>}
      body={
        <>
          <Paragraph css={{ marginBottom: 'default' }}>
            Select the property you want to enroll in online payments, or add a property if you don’t see the one you
            want listed.
          </Paragraph>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <Box css={{ gridColumnEnd: { base: 'span 3', md: 'span 2' } }}>
              <FormField
                css={{ marginBottom: 'default' }}
                label={<Label>Select property</Label>}
                control={
                  <Select
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedListingAlias(e.target.value)}
                    value={selectedListingAlias}
                  >
                    {options}
                  </Select>
                }
              />
              <TextButton css={{ paddingInline: 'none' }} icon={<IconPlusCircleFilled />} onClick={onAddPropertyClick}>
                Add a property
              </TextButton>
            </Box>
          </Box>
        </>
      }
      footer={
        <ButtonGroup aria-label="Select property">
          <TextButton onClick={onClose}>Cancel</TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              if (user) {
                emitClickContinueEvent(user);
              }
              const pathToRoute = `/rental-manager/payments/properties/${selectedListingAlias}`;

              if (e.metaKey || e.ctrlKey) {
                window.open(pathToRoute, '_blank', 'noopener, noreferrer');
              } else {
                window.location.href = pathToRoute;
              }
              onClose();
            }}
          >
            Confirm
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default SelectProperty;
